/**
 * Lazy-load Leaflet (JS + CSS) from a CDN the first time a widget needs it.
 *
 * We deliberately keep Leaflet *out* of the bundle so cards that don't use
 * a map don't pay for it (~40KB gzip). Per the coding guidelines (rule 10)
 * the bundle stays small; the map widget pulls Leaflet on demand.
 *
 * Subsequent calls return the cached promise — Leaflet is only fetched once
 * per page load no matter how many `<div data-conny-widget="berlin-map">`
 * elements are mounted.
 */
const LEAFLET_VERSION = "1.9.4";
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;

const CLUSTER_VERSION = "1.5.3";
const CLUSTER_JS = `https://unpkg.com/leaflet.markercluster@${CLUSTER_VERSION}/dist/leaflet.markercluster.js`;
// Base CSS only (cluster zoom/spiderfy transitions). We deliberately skip
// MarkerCluster.Default.css — the widget brings its own cluster icon styles.
const CLUSTER_CSS = `https://unpkg.com/leaflet.markercluster@${CLUSTER_VERSION}/dist/MarkerCluster.css`;

// Minimal subset of the Leaflet API the widget actually uses. We don't pull
// in `@types/leaflet` here — keeping the public surface tiny avoids a hard
// dependency on the typings package at build time.
export interface LeafletNS {
  map: (
    el: HTMLElement | string,
    options?: Record<string, unknown>,
  ) => LeafletMap;
  tileLayer: (
    urlTemplate: string,
    options?: Record<string, unknown>,
  ) => LeafletLayer;
  circleMarker: (
    latlng: [number, number],
    options?: Record<string, unknown>,
  ) => LeafletMarker;
  marker: (
    latlng: [number, number],
    options?: Record<string, unknown>,
  ) => LeafletMarker;
  control: {
    zoom: (options?: Record<string, unknown>) => LeafletControl;
  };
  divIcon: (options?: Record<string, unknown>) => unknown;
  /** Added by the markercluster plugin — present after `loadMarkerCluster()`. */
  markerClusterGroup?: (
    options?: Record<string, unknown>,
  ) => LeafletClusterGroup;
}

/** A cluster as handed to `iconCreateFunction`. */
export interface LeafletCluster {
  getChildCount(): number;
  getAllChildMarkers(): LeafletMarker[];
}

export interface LeafletClusterGroup {
  addTo(map: LeafletMap): LeafletClusterGroup;
  addLayers(layers: LeafletMarker[]): LeafletClusterGroup;
  clearLayers(): LeafletClusterGroup;
}

export interface LeafletMap {
  setView(
    latlng: [number, number],
    zoom?: number,
    options?: unknown,
  ): LeafletMap;
  fitBounds(
    bounds: [[number, number], [number, number]],
    options?: unknown,
  ): LeafletMap;
  removeLayer(layer: LeafletLayer | LeafletMarker): LeafletMap;
  remove(): void;
  invalidateSize(): void;
}

export interface LeafletLayer {
  addTo(map: LeafletMap): LeafletLayer;
}

export interface LeafletMarker {
  addTo(map: LeafletMap): LeafletMarker;
  bindPopup(html: string): LeafletMarker;
  openPopup(): LeafletMarker;
}

export interface LeafletControl {
  addTo(map: LeafletMap): LeafletControl;
}

declare global {
  interface Window {
    L?: LeafletNS;
  }
}

let cached: Promise<LeafletNS> | null = null;
let cachedCluster: Promise<LeafletNS> | null = null;

export function loadLeaflet(): Promise<LeafletNS> {
  if (cached) return cached;
  cached = doLoad();
  return cached;
}

/**
 * Load the markercluster plugin (JS + base CSS) on top of Leaflet. Resolves
 * with the same `L` namespace, now guaranteed to carry `markerClusterGroup`.
 * Cached the same way as Leaflet itself — one fetch per page load.
 */
export function loadMarkerCluster(): Promise<LeafletNS> {
  if (cachedCluster) return cachedCluster;
  cachedCluster = loadLeaflet().then((L) => {
    if (L.markerClusterGroup) return L;
    injectCss(CLUSTER_CSS, "data-conny-leaflet-cluster");
    return injectScript(CLUSTER_JS, "data-conny-leaflet-cluster", () =>
      Boolean(window.L?.markerClusterGroup),
    ).then(() => {
      if (!window.L?.markerClusterGroup) {
        throw new Error(
          "markercluster script loaded but L.markerClusterGroup is undefined",
        );
      }
      return window.L;
    });
  });
  return cachedCluster;
}

function doLoad(): Promise<LeafletNS> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Leaflet can only be loaded in the browser"));
      return;
    }
    if (window.L) {
      resolve(window.L);
      return;
    }
    injectCss(LEAFLET_CSS, "data-conny-leaflet");
    injectScript(LEAFLET_JS, "data-conny-leaflet", () => Boolean(window.L))
      .then(() => {
        if (window.L) resolve(window.L);
        else
          reject(new Error("Leaflet script loaded but window.L is undefined"));
      })
      .catch(reject);
  });
}

function injectCss(href: string, marker: string): void {
  if (document.querySelector(`link[${marker}="1"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute(marker, "1");
  document.head.appendChild(link);
}

function injectScript(
  src: string,
  marker: string,
  ready: () => boolean,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[${marker}="1"]`,
    );
    if (existing) {
      // The tag may have finished loading already — `load` won't re-fire.
      if (ready()) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load " + src)),
      );
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.setAttribute(marker, "1");
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", () =>
      reject(new Error("Failed to load " + src)),
    );
    document.head.appendChild(s);
  });
}
